import { jsx as _jsx } from "react/jsx-runtime";
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCallback, useRef } from 'react';
import { useComboboxContext } from './Combobox';
import { scrub } from './Combobox.helpers';
import { useGroupedData, useRegisterKeyboardNavigation } from './Combobox.hooks';
import ComboboxListItem, { ComboboxListGroup } from './ComboboxListItem';
function ComboboxVirtualList(_a) {
    var data = _a.data;
    var containerRef = useRef(null);
    var _b = useComboboxContext(), useGrouping = _b.useGrouping, useMultipleSelect = _b.useMultipleSelect, usePillList = _b.usePillList, onAdd = _b.onAdd, onChange = _b.onChange, areAllSelected = _b.areAllSelected, selectAll = _b.selectAll, testSelector = _b.testSelector, addItemLabel = _b.addItemLabel;
    var groupedItems = useGroupedData(data, useGrouping);
    var groupMap = Object.keys(groupedItems);
    var handleSelectAll = useCallback(function () { return (areAllSelected ? onChange(null) : selectAll()); }, [areAllSelected, selectAll, onChange]);
    var indexOffset = useMultipleSelect && !usePillList ? 1 : 0;
    var count = (useGrouping ? Object.keys(groupedItems).length : data.length) +
        indexOffset +
        (addItemLabel ? 1 : 0);
    var rowVirtualizer = useVirtualizer({
        count: count,
        getScrollElement: function () { return containerRef.current; },
        estimateSize: function () { return 32; },
    });
    var _c = useRegisterKeyboardNavigation(data, rowVirtualizer, indexOffset, handleSelectAll), activeIndex = _c.activeIndex, setActiveIndex = _c.setActiveIndex;
    var onItemHover = function (index) { return setActiveIndex(index); };
    return (_jsx("div", { className: "flex-grow overflow-y-auto max-h-80 bg-gradient-to-br from-white to-slate-50 basis-1/2", ref: containerRef, children: _jsx("ul", { className: "relative p-0 m-0", style: { height: "".concat(rowVirtualizer.getTotalSize(), "px") }, "data-cy": "".concat(testSelector, "-list"), children: rowVirtualizer.getVirtualItems().map(function (virtualItem) {
                if (virtualItem.index === 0 && useMultipleSelect && !usePillList) {
                    return (_jsx(SelectAllListItem, { isGrouping: useGrouping, onChange: handleSelectAll, onItemHover: onItemHover, areAllSelected: areAllSelected, virtualItem: virtualItem, activeIndex: activeIndex }, virtualItem.index));
                }
                if (addItemLabel && virtualItem.index === count - 1) {
                    return (_jsx(ComboboxListItem, { dataItem: {
                            label: addItemLabel,
                            value: addItemLabel,
                        }, onChange: onAdd, onHover: function () { return onItemHover(virtualItem.index); }, isActive: virtualItem.index === activeIndex, className: clsx('absolute top-0 left-0 w-full', virtualItem.index !== activeIndex && 'text-sky-500', count === 1 && 'justify-end'), style: {
                            height: "".concat(virtualItem.size, "px"),
                            transform: "translateY(".concat(virtualItem.start, "px)"),
                        }, hideSelectedIcon: true }, virtualItem.index));
                }
                return useGrouping ? (_jsx("ul", { className: "p-0 m-0", "data-cy": "".concat(testSelector, "-group-").concat(scrub(groupMap[virtualItem.index - indexOffset])), ref: virtualItem.measureElement, style: {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: "translateY(".concat(virtualItem.start, "px)"),
                    }, children: _jsx(ComboboxListGroup, { groupName: groupMap[virtualItem.index - indexOffset], data: groupedItems[groupMap[virtualItem.index - indexOffset]] }) }, virtualItem.index)) : (_jsx(ComboboxListItem, { dataItem: data[virtualItem.index - indexOffset], onChange: onChange, onHover: function () { return onItemHover(virtualItem.index); }, isActive: virtualItem.index === activeIndex, className: "absolute top-0 left-0 w-full", style: {
                        height: "".concat(virtualItem.size, "px"),
                        transform: "translateY(".concat(virtualItem.start, "px)"),
                    } }, virtualItem.index));
            }) }) }));
}
var SelectAllListItem = function (_a) {
    var isGrouping = _a.isGrouping, onChange = _a.onChange, onItemHover = _a.onItemHover, areAllSelected = _a.areAllSelected, activeIndex = _a.activeIndex, virtualItem = _a.virtualItem;
    return (_jsx(ComboboxListItem, { dataItem: {
            label: 'Select all',
            value: 'Select all',
        }, onChange: onChange, onHover: function () { return !isGrouping && onItemHover(0); }, overrideSelected: areAllSelected, isActive: !isGrouping && activeIndex === 0, className: clsx(isGrouping
            ? 'absolute top-0 left-0 w-full border-b border-slate-200 hover:bg-sky-500 hover:text-white'
            : 'absolute top-0 left-0 w-full border-b border-slate-200'), style: {
            height: "".concat(virtualItem.size, "px"),
            transform: "translateY(".concat(virtualItem.start, "px)"),
        } }));
};
export { ComboboxVirtualList as default };
